import { loanAutomationServices } from '@/services';

const normalizePaginatedListResponse = (axiosResponse) => {
  const body = axiosResponse?.data;

  if (!body) {
    return { items: [], meta: {} };
  }

  // { status, message, data: { data: [], total, page, pageSize } }
  if (body.data && Array.isArray(body.data.data)) {
    return { items: body.data.data, meta: body.data };
  }

  // { data: [], total, page, pageSize }
  if (Array.isArray(body.data)) {
    return { items: body.data, meta: body };
  }

  return { items: [], meta: {} };
};

const buildListPagination = (listPayload, defaults) => {
  const page = listPayload.page ?? defaults.page;
  const pageSize = listPayload.pageSize ?? defaults.pageSize;
  const total = listPayload.total ?? 0;
  const totalPages =
    listPayload.totalPages ?? (pageSize > 0 ? Math.ceil(total / pageSize) : 0);

  return {
    total,
    page,
    pageSize,
    totalPages,
    hasPreviousPage: listPayload.hasPreviousPage ?? page > 1,
    hasNextPage: listPayload.hasNextPage ?? page * pageSize < total,
  };
};

export default {
  namespaced: true,
  state: () => ({
    loanProducts: [],
    creditPolicies: [],
    policyParameters: [],
    approvalWorkflows: [],
    currentWorkflow: null,
    currentCreditPolicy: null,
    pagination: {
      total: 0,
      page: 1,
      pageSize: 10,
      totalPages: 0,
      hasPreviousPage: false,
      hasNextPage: false,
    },
  }),

  getters: {
    loanProducts: ({ loanProducts }) => loanProducts,
    creditPolicies: ({ creditPolicies }) => creditPolicies,
    policyParameters: ({ policyParameters }) => policyParameters,
    approvalWorkflows: ({ approvalWorkflows }) => approvalWorkflows,
    currentWorkflow: ({ currentWorkflow }) => currentWorkflow,
    currentCreditPolicy: ({ currentCreditPolicy }) => currentCreditPolicy,
    pagination: ({ pagination }) => pagination,
  },

  mutations: {
    SET_LOAN_PRODUCTS(state, products) {
      state.loanProducts = products;
    },
    SET_CREDIT_POLICIES(state, policies) {
      state.creditPolicies = policies;
    },
    SET_POLICY_PARAMETERS(state, parameters) {
      state.policyParameters = parameters;
    },
    SET_APPROVAL_WORKFLOWS(state, workflows) {
      state.approvalWorkflows = workflows;
    },
    SET_CURRENT_WORKFLOW(state, workflow) {
      state.currentWorkflow = workflow;
    },
    SET_CURRENT_CREDIT_POLICY(state, policy) {
      state.currentCreditPolicy = policy;
    },
    SET_PAGINATION(state, paginationData) {
      state.pagination = paginationData;
    },
  },

  actions: {
    async getLoanProducts({ commit }, { page = 1, pageSize = 10, search = '' }) {
      try {
        const response = await loanAutomationServices.getLoanProducts(page, pageSize, search);

        commit('SET_LOAN_PRODUCTS', response.items);
        commit('SET_PAGINATION', {
          total: response.total,
          page: response.page,
          pageSize: response.pageSize,
          totalPages: response.totalPages,
          hasPreviousPage: response.hasPreviousPage,
          hasNextPage: response.hasNextPage,
        });

        return response;
      } catch (error) {
        console.error('Error fetching loan products:', error);
        throw error;
      }
    },

    async getCreditPolicies({ commit }, { page = 1, pageSize = 10, search = '' }) {
      try {
        const response = await loanAutomationServices.getCreditPolicies(
          page,
          pageSize,
          search
        );
        const { items, meta } = normalizePaginatedListResponse(response);

        commit('SET_CREDIT_POLICIES', items);
        commit('SET_PAGINATION', buildListPagination(meta, { page, pageSize }));

        return response;
      } catch (error) {
        console.error('Error fetching credit policies:', error);
        throw error;
      }
    },

    async getPolicyParameters(
      { commit },
      { page = 1, pageSize = 10, search = '', sortBy = '', orderBy = '' }
    ) {
      try {
        const response = await loanAutomationServices.getPolicyParameters(
          page,
          pageSize,
          search,
          sortBy,
          orderBy
        );
        const { items, meta } = normalizePaginatedListResponse(response);

        commit('SET_POLICY_PARAMETERS', items);
        commit('SET_PAGINATION', buildListPagination(meta, { page, pageSize }));
        return response;
      } catch (error) {
        console.error('Error fetching policy parameters:', error);
        throw error;
      }
    },

    async createPolicyParameter({ commit }, parameterData) {
      try {
        const response = await loanAutomationServices.createPolicyParameter(parameterData);
        return response;
      } catch (error) {
        console.error('Error creating policy parameter:', error);
        throw error;
      }
    },

    async updatePolicyParameter({ commit }, { id, data }) {
      try {
        const response = await loanAutomationServices.updatePolicyParameter(id, data);
        return response;
      } catch (error) {
        console.error('Error updating policy parameter:', error);
        throw error;
      }
    },

    async getApprovalWorkflows({ commit }, { page = 1, pageSize = 10, search = '' }) {
      try {
        const response = await loanAutomationServices.getApprovalWorkflows(
          page,
          pageSize,
          search
        );

        commit('SET_APPROVAL_WORKFLOWS', response.data.data);
        commit('SET_PAGINATION', {
          total: response.data.total,
          page: response.data.page,
          pageSize: response.data.pageSize,
          totalPages: response.data.totalPages,
          hasPreviousPage: response.data.hasPreviousPage,
          hasNextPage: response.data.hasNextPage,
        });

        return response;
      } catch (error) {
        console.error('Error fetching approval workflows:', error);
        throw error;
      }
    },

    async createApprovalWorkflow({ commit }, workflowData) {
      try {
        const response = await loanAutomationServices.createApprovalWorkflow(workflowData);
        return response;
      } catch (error) {
        console.error('Error creating approval workflow:', error);
        throw error;
      }
    },

    async getApprovalWorkflowById({ commit }, id) {
      try {
        const response = await loanAutomationServices.getApprovalWorkflowById(id);
        commit('SET_CURRENT_WORKFLOW', response.data);
        return response;
      } catch (error) {
        console.error('Error fetching approval workflow:', error);
        throw error;
      }
    },

    async updateApprovalWorkflow({ commit }, { id, data }) {
      try {
        const response = await loanAutomationServices.updateApprovalWorkflow(id, data);
        return response;
      } catch (error) {
        console.error('Error updating approval workflow:', error);
        throw error;
      }
    },

    async getCreditPolicyById({ commit }, id) {
      try {
        const response = await loanAutomationServices.getCreditPolicyById(id);
        commit('SET_CURRENT_CREDIT_POLICY', response.data.data);
        return response;
      } catch (error) {
        console.error('Error fetching credit policy:', error);
        throw error;
      }
    },

    async updateCreditPolicy({ commit }, { id, data }) {
      try {
        const response = await loanAutomationServices.updateCreditPolicy(id, data);
        return response;
      } catch (error) {
        console.error('Error updating credit policy:', error);
        throw error;
      }
    },

    async getWorkflowMappings({ commit }, loanProductId) {
      try {
        const response = await loanAutomationServices.getWorkflowMappings(loanProductId);
        return response;
      } catch (error) {
        console.error('Error fetching workflow mappings:', error);
        throw error;
      }
    },

    async createWorkflowMapping({ commit }, { loanProductId, data }) {
      try {
        const response = await loanAutomationServices.createWorkflowMapping(
          loanProductId,
          data
        );
        return response;
      } catch (error) {
        console.error('Error creating workflow mapping:', error);
        throw error;
      }
    },

    async updateWorkflowMapping({ commit }, { loanProductId, workflowId, data }) {
      try {
        const response = await loanAutomationServices.updateWorkflowMapping(
          loanProductId,
          workflowId,
          data
        );
        return response;
      } catch (error) {
        console.error('Error updating workflow mapping:', error);
        throw error;
      }
    },

    async deleteWorkflowMapping({ commit }, { loanProductId, workflowId }) {
      try {
        const response = await loanAutomationServices.deleteWorkflowMapping(
          loanProductId,
          workflowId
        );
        return response;
      } catch (error) {
        console.error('Error deleting workflow mapping:', error);
        throw error;
      }
    },
  },
};
